declare const window: any;
import type { TableSchema, TableSchemaCategory, TableCellSchema, TableRowSchema, TableColSchema, MergedRegion } from './tableSchema.types';
import type { TableCellStyle } from '../../models/table/tableCell.types';
import type { Node } from '../../../../core/models/scene';

const STORAGE_KEY = 'creately-custom-table-schemas';

export interface CustomTableSchema extends TableSchema {
  id: string;
  name: string;
  category: TableSchemaCategory;
  createdAt: number;
  updatedAt: number;
}

const validateSchema = (schema: unknown): schema is CustomTableSchema => {
  if (!schema || typeof schema !== 'object') return false;
  const s = schema as Record<string, unknown>;

  if (typeof s.id !== 'string' || s.id.trim().length === 0) return false;
  if (typeof s.name !== 'string' || s.name.trim().length === 0) return false;
  if (s.category !== 'strategy' && s.category !== 'agile' && s.category !== 'planning' && s.category !== 'analysis' && s.category !== 'custom') {
    return false;
  }

  if (!s.grid || typeof s.grid !== 'object') return false;
  const grid = s.grid as Record<string, unknown>;
  if (typeof grid.rows !== 'number' || grid.rows < 1) return false;
  if (typeof grid.cols !== 'number' || grid.cols < 1) return false;

  if (s.mergedRegions && Array.isArray(s.mergedRegions)) {
    for (const region of s.mergedRegions as unknown[]) {
      if (!region || typeof region !== 'object') return false;
      const r = region as Record<string, unknown>;
      if (typeof r.startRow !== 'number' || typeof r.startCol !== 'number' || typeof r.endRow !== 'number' || typeof r.endCol !== 'number') {
        return false;
      }
      if (r.startRow < 0 || r.startCol < 0 || r.endRow < r.startRow || r.endCol < r.startCol) {
        return false;
      }
      if (r.endRow >= grid.rows || r.endCol >= grid.cols) {
        return false;
      }
    }
  }

  if (s.cells && Array.isArray(s.cells)) {
    for (const cell of s.cells as unknown[]) {
      if (!cell || typeof cell !== 'object') return false;
      const c = cell as Record<string, unknown>;
      if (typeof c.row !== 'number' || typeof c.col !== 'number') return false;
      if (c.row < 0 || c.row >= grid.rows || c.col < 0 || c.col >= grid.cols) {
        return false;
      }
    }
  }

  if (s.rows && Array.isArray(s.rows)) {
    for (const row of s.rows as unknown[]) {
      if (!row || typeof row !== 'object') return false;
      const r = row as Record<string, unknown>;
      if (typeof r.index !== 'number') return false;
      if (r.index < 0 || r.index >= grid.rows) return false;
    }
  }

  if (s.cols && Array.isArray(s.cols)) {
    for (const col of s.cols as unknown[]) {
      if (!col || typeof col !== 'object') return false;
      const c = col as Record<string, unknown>;
      if (typeof c.index !== 'number') return false;
      if (c.index < 0 || c.index >= grid.cols) return false;
    }
  }

  return true;
};

const loadFromStorage = (): CustomTableSchema[] => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(validateSchema);
  } catch (error) {
    console.error('[CustomTableSchemas] Failed to load from storage:', error);
    return [];
  }
};

const saveToStorage = (schemas: CustomTableSchema[]): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(schemas));
  } catch (error) {
    console.error('[CustomTableSchemas] Failed to save to storage:', error);
  }
};

export const loadCustomSchemas = (): CustomTableSchema[] => {
  return loadFromStorage();
};

export const saveCustomSchema = (schema: Omit<CustomTableSchema, 'createdAt' | 'updatedAt'>): CustomTableSchema => {
  const existing = loadFromStorage();
  const existingIndex = existing.findIndex((s) => s.id === schema.id);

  const now = Date.now();
  const customSchema: CustomTableSchema = {
    ...schema,
    createdAt: existingIndex >= 0 ? existing[existingIndex].createdAt : now,
    updatedAt: now,
  };

  if (existingIndex >= 0) {
    existing[existingIndex] = customSchema;
  } else {
    existing.push(customSchema);
  }

  saveToStorage(existing);
  return customSchema;
};

export const deleteCustomSchema = (schemaId: string): boolean => {
  const existing = loadFromStorage();
  const index = existing.findIndex((s) => s.id === schemaId);
  if (index < 0) return false;
  existing.splice(index, 1);
  saveToStorage(existing);
  return true;
};

export const getCustomSchema = (schemaId: string): CustomTableSchema | null => {
  const existing = loadFromStorage();
  return existing.find((s) => s.id === schemaId) ?? null;
};

export const exportSchemaAsJSON = (schema: CustomTableSchema): string => {
  const { createdAt, updatedAt, ...exportable } = schema;
  return JSON.stringify(exportable, null, 2);
};

export const importSchemaFromJSON = (json: string): CustomTableSchema | null => {
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!validateSchema(parsed)) return null;
    const now = Date.now();
    return {
      ...parsed,
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error('[CustomTableSchemas] Failed to import schema:', error);
    return null;
  }
};

const parseCellKey = (key: string): { r: number; c: number } | null => {
  const [rStr, cStr] = key.split(',');
  const r = Number(rStr);
  const c = Number(cStr);
  if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
  return { r, c };
};


export const extractSchemaFromTableNode = (
  node: Node,
  schemaId: string,
  name: string,
  category: TableSchemaCategory = 'custom',
): Omit<CustomTableSchema, 'createdAt' | 'updatedAt'> | null => {
  if (node.type !== 'table') return null;

  const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
  const rowCount = typeof attrs.rowCount === 'number' && Number.isFinite(attrs.rowCount) ? Math.max(1, Math.floor(attrs.rowCount)) : 3;
  const columnCount = typeof attrs.columnCount === 'number' && Number.isFinite(attrs.columnCount) ? Math.max(1, Math.floor(attrs.columnCount)) : 3;

  const showHeader = attrs.showHeader !== false;
  const showHeaderColumn = (attrs as any).showHeaderColumn === true;
  const headerRowIndices = (Array.isArray((attrs as any).headerRowIndices) ? (attrs as any).headerRowIndices : []) as number[];
  const headerColIndices = (Array.isArray((attrs as any).headerColIndices) ? (attrs as any).headerColIndices : []) as number[];

  const rowHeights = (Array.isArray(attrs.rowHeights) ? attrs.rowHeights : []) as number[];
  const columnWidths = (Array.isArray(attrs.columnWidths) ? attrs.columnWidths : []) as number[];

  const defaultRowHeight = rowHeights.length > 0 ? Math.round(rowHeights.reduce((a, b) => a + b, 0) / rowHeights.length) : undefined;
  const defaultColWidth = columnWidths.length > 0 ? Math.round(columnWidths.reduce((a, b) => a + b, 0) / columnWidths.length) : undefined;

  const minCellHeight = rowHeights.length > 0 ? Math.min(...rowHeights.filter((h) => h > 0)) : undefined;
  const minCellWidth = columnWidths.length > 0 ? Math.min(...columnWidths.filter((w) => w > 0)) : undefined;

  const headerHeight = typeof attrs.headerHeight === 'number' && Number.isFinite(attrs.headerHeight) ? attrs.headerHeight : undefined;

  const cellText = ((attrs as any).cellText ?? {}) as Record<string, string>;
  const cellStyles = ((attrs as any).cellStyles ?? {}) as Record<string, unknown>;
  const cellLayouts = ((attrs as any).cellLayouts ?? {}) as Record<string, unknown>;

  const mergedRegions = (Array.isArray((attrs as any).mergedRegions) ? (attrs as any).mergedRegions : []) as MergedRegion[];

  const rows: TableRowSchema[] = [];
  if (showHeader && headerRowIndices.includes(0)) {
    rows.push({
      index: 0,
      isHeader: true,
      height: headerHeight,
    });
  }

  const cols: TableColSchema[] = [];
  if (showHeaderColumn && headerColIndices.includes(0)) {
    cols.push({
      index: 0,
      isHeader: true,
      width: columnWidths[0],
    });
  }

  const cells: TableCellSchema[] = [];
  for (const [key, text] of Object.entries(cellText)) {
    const parsed = parseCellKey(key);
    if (!parsed) continue;
    if (parsed.r < 0 || parsed.r >= rowCount || parsed.c < 0 || parsed.c >= columnCount) continue;

    const cell: TableCellSchema = {
      row: parsed.r,
      col: parsed.c,
      text: typeof text === 'string' ? text : undefined,
    };

    const style = cellStyles[key];
    if (style && typeof style === 'object') {
      const s = style as Record<string, unknown>;
      const nextStyle: TableCellStyle = {};
      if (typeof s.fillColor === 'string') nextStyle.fillColor = s.fillColor;
      if (typeof s.textColor === 'string') nextStyle.textColor = s.textColor;
      if (typeof s.fontFamily === 'string') nextStyle.fontFamily = s.fontFamily;
      if (s.fontWeight === 'normal' || s.fontWeight === 'bold') nextStyle.fontWeight = s.fontWeight;
      if (s.fontStyle === 'normal' || s.fontStyle === 'italic') nextStyle.fontStyle = s.fontStyle;
      if (
        s.textDecoration === 'underline' ||
        s.textDecoration === 'line-through' ||
        s.textDecoration === 'underline line-through' ||
        s.textDecoration === 'none'
      ) {
        nextStyle.textDecoration = s.textDecoration;
      }
      if (s.textAlign === 'left' || s.textAlign === 'center' || s.textAlign === 'right') nextStyle.textAlign = s.textAlign;
      if (s.verticalAlign === 'top' || s.verticalAlign === 'middle' || s.verticalAlign === 'bottom') {
        nextStyle.verticalAlign = s.verticalAlign;
      }
      if (typeof s.fontSize === 'number' && Number.isFinite(s.fontSize)) nextStyle.fontSize = s.fontSize;
      if (typeof s.padding === 'number' && Number.isFinite(s.padding)) nextStyle.padding = s.padding;
      if (typeof s.paddingTop === 'number' && Number.isFinite(s.paddingTop)) nextStyle.paddingTop = s.paddingTop;
      if (typeof s.paddingRight === 'number' && Number.isFinite(s.paddingRight)) nextStyle.paddingRight = s.paddingRight;
      if (typeof s.paddingBottom === 'number' && Number.isFinite(s.paddingBottom)) nextStyle.paddingBottom = s.paddingBottom;
      if (typeof s.paddingLeft === 'number' && Number.isFinite(s.paddingLeft)) nextStyle.paddingLeft = s.paddingLeft;
      if (typeof s.margin === 'number' && Number.isFinite(s.margin)) nextStyle.margin = s.margin;
      if (typeof s.marginTop === 'number' && Number.isFinite(s.marginTop)) nextStyle.marginTop = s.marginTop;
      if (typeof s.marginRight === 'number' && Number.isFinite(s.marginRight)) nextStyle.marginRight = s.marginRight;
      if (typeof s.marginBottom === 'number' && Number.isFinite(s.marginBottom)) nextStyle.marginBottom = s.marginBottom;
      if (typeof s.marginLeft === 'number' && Number.isFinite(s.marginLeft)) nextStyle.marginLeft = s.marginLeft;
      if (typeof s.borderColor === 'string') nextStyle.borderColor = s.borderColor;
      if (typeof s.borderWidth === 'number' && Number.isFinite(s.borderWidth)) nextStyle.borderWidth = s.borderWidth;
      if (Array.isArray(s.borderDash) && s.borderDash.every((entry) => typeof entry === 'number' && Number.isFinite(entry))) {
        nextStyle.borderDash = s.borderDash as number[];
      }
      if (typeof s.textRotation === 'number' && (s.textRotation === 0 || s.textRotation === 90 || s.textRotation === -90 || s.textRotation === 180)) {
        nextStyle.textRotation = s.textRotation as 0 | 90 | -90 | 180;
        cell.textRotation = s.textRotation as 0 | 90 | -90 | 180;
      }
      if (Object.keys(nextStyle).length > 0) cell.style = nextStyle;
    }

    const layout = cellLayouts[key];
    if (layout && typeof layout === 'object') {
      const l = layout as Record<string, unknown>;
      if (l.algorithmId === 'grid' || l.algorithmId === 'fixed') {
        cell.layout = {
          algorithmId: l.algorithmId as 'fixed' | 'grid',
          params: typeof l.params === 'object' ? (l.params as Record<string, unknown>) : undefined,
        };
      }
    }

    cells.push(cell);
  }

  const headerFillColor = typeof attrs.headerFillColor === 'string' ? attrs.headerFillColor : undefined;
  const gridColor = typeof (attrs as any).gridColor === 'string' ? (attrs as any).gridColor : undefined;
  const strokeWidth = typeof (attrs as any).strokeWidth === 'number' && Number.isFinite((attrs as any).strokeWidth) ? (attrs as any).strokeWidth : undefined;

  const schema: Omit<CustomTableSchema, 'createdAt' | 'updatedAt'> = {
    id: schemaId,
    name,
    category,
    grid: {
      rows: rowCount,
      cols: columnCount,
      rowsExpandable: true,
      colsExpandable: true,
      minRows: 1,
      minCols: 1,
    },
    dimensions: {
      defaultRowHeight: defaultRowHeight ?? 40,
      defaultColWidth: defaultColWidth ?? 100,
      ...(minCellHeight ? { minCellHeight } : {}),
      ...(minCellWidth ? { minCellWidth } : {}),
    },
    ...(cells.length > 0 ? { cells } : {}),
    ...(rows.length > 0 ? { rows } : {}),
    ...(cols.length > 0 ? { cols } : {}),
    ...(mergedRegions.length > 0 ? { mergedRegions } : {}),
    theme: {
      ...(headerFillColor ? { headerBackground: headerFillColor } : {}),
      ...(gridColor ? { borderColor: gridColor } : {}),
      ...(strokeWidth ? { borderWidth: strokeWidth } : {}),
    },
  };

  return schema;
};
